import React from "react";
import classNames from "classnames";
import { Spinner } from "@shared/ui/spinner";
import { useWindowControls } from "../hooks/useWindowControls";
import type {
    ActionSheetDrillInItem,
    ActionSheetItem,
    ActionSheetItemContext,
    ActionSheetProps,
    ActionSheetSection,
    ActionSheetToggleItem,
} from "./types";
import "./ActionSheet.scss";
import { Button } from "@src/shared/ui/button";
import { Icon } from "@src/shared/ui/icon";

interface ActionSheetScreen {
    title?: React.ReactNode;
    supportingTitle?: React.ReactNode;
    sections: ActionSheetSection[];
}

function isNonEmptySection(section: ActionSheetSection): boolean {
    return section.items.length > 0;
}

function getRootScreen(props: ActionSheetProps): ActionSheetScreen {
    return {
        title: props.title,
        supportingTitle: props.supportingTitle,
        sections: props.sections,
    };
}

interface ActionSheetRowProps {
    item: ActionSheetItem;
    context: ActionSheetItemContext;
    onDrillIn: (item: ActionSheetDrillInItem) => void;
}

interface ActionSheetStandardRowContentProps {
    item: Exclude<ActionSheetItem, { type: "custom" }>;
    trailingSlot?: React.ReactNode;
}

const ActionSheetStandardRowContent: React.FC<ActionSheetStandardRowContentProps> = ({ item, trailingSlot }) => (
    <>
        {item.icon != null && <span className="action_sheet__icon">{item.icon}</span>}
        <span className="action_sheet__text">
            <span className="action_sheet__label">{item.label}</span>
            {item.description != null && <span className="action_sheet__description">{item.description}</span>}
        </span>
        {trailingSlot}
    </>
);

interface ActionSheetToggleRowProps {
    item: ActionSheetToggleItem;
}

const ActionSheetToggleRow: React.FC<ActionSheetToggleRowProps> = ({ item }) => {
    const [checked, setChecked] = React.useState(item.checked);
    const isDisabled = item.disabled === true;

    React.useEffect(() => {
        setChecked(item.checked);
    }, [item.checked]);

    const handleToggle = () => {
        const nextChecked = !checked;
        setChecked(nextChecked);
        item.onCheckedChange(nextChecked);
    };

    return (
        <button type="button" className="action_sheet__row" disabled={isDisabled} onClick={handleToggle}>
            <ActionSheetStandardRowContent
                item={item}
                trailingSlot={
                    <span className="action_sheet__switch" aria-hidden="true" data-checked={checked ? "true" : "false"}>
                        <span className="action_sheet__switch_thumb" />
                    </span>
                }
            />
        </button>
    );
};

const ActionSheetRow: React.FC<ActionSheetRowProps> = ({ item, context, onDrillIn }) => {
    if (item.type === "custom") {
        return <div className="action_sheet__custom_item">{item.render(context)}</div>;
    }

    const isDisabled = item.disabled === true || (item.type === "action" && item.loading === true);

    if (item.type === "toggle") {
        return <ActionSheetToggleRow item={item} />;
    }

    if (item.type === "drill-in") {
        return (
            <button type="button" className="action_sheet__row" disabled={isDisabled} onClick={() => onDrillIn(item)}>
                <ActionSheetStandardRowContent
                    item={item}
                    trailingSlot={
                        <span className="action_sheet__chevron" aria-hidden="true">
                            ›
                        </span>
                    }
                />
            </button>
        );
    }

    const handleSelect = () => {
        const shouldClose = item.shouldCloseOnSelect !== false;
        if (shouldClose) {
            context.close();
        }
        void item.onSelect?.(context);
    };

    return (
        <button
            type="button"
            className={classNames("action_sheet__row", {
                "action_sheet__row--danger": item.tone === "danger",
            })}
            disabled={isDisabled}
            onClick={handleSelect}
        >
            <ActionSheetStandardRowContent
                item={item}
                trailingSlot={item.loading === true && <Spinner size="sm" className="action_sheet__spinner" />}
            />
        </button>
    );
};

/**
 * Универсальный mobile action sheet: короткие команды, настройки и лёгкий drill-in внутри одного overlay.
 */
export const ActionSheet: React.FC<ActionSheetProps> = ({ className, registerBackHandler, ...props }) => {
    const windowControls = useWindowControls();
    const [screenStack, setScreenStack] = React.useState<ActionSheetScreen[]>(() => [getRootScreen(props)]);
    const currentScreen = screenStack[screenStack.length - 1];
    const visibleSections = currentScreen.sections.filter(isNonEmptySection);
    const canGoBack = screenStack.length > 1;

    React.useEffect(() => {
        setScreenStack([getRootScreen(props)]);
    }, [props.sections, props.supportingTitle, props.title]);

    const goBack = React.useCallback(() => {
        setScreenStack((prev) => {
            if (prev.length <= 1) return prev;
            return prev.slice(0, -1);
        });
    }, []);

    const handleAppBack = React.useCallback((): boolean => {
        if (!canGoBack) {
            return false;
        }

        goBack();
        return true;
    }, [canGoBack, goBack]);

    React.useEffect(() => {
        if (registerBackHandler == null) {
            return undefined;
        }

        return registerBackHandler(handleAppBack);
    }, [handleAppBack, registerBackHandler]);

    const context = React.useMemo<ActionSheetItemContext>(
        () => ({
            close: windowControls.close,
            goBack,
            canGoBack,
        }),
        [canGoBack, goBack, windowControls.close],
    );

    const handleDrillIn = React.useCallback((item: ActionSheetDrillInItem) => {
        setScreenStack((prev) => [
            ...prev,
            {
                title: item.title ?? item.label,
                supportingTitle: item.supportingTitle,
                sections: item.sections,
            },
        ]);
    }, []);

    return (
        <div className={classNames("action_sheet", className)}>
            {canGoBack && (currentScreen.title != null || currentScreen.supportingTitle != null) && (
                <div className="action_sheet__screen_header">
                    {currentScreen.title != null && <h3 className="action_sheet__screen_title">{currentScreen.title}</h3>}
                    {currentScreen.supportingTitle != null && (
                        <p className="action_sheet__screen_description">{currentScreen.supportingTitle}</p>
                    )}
                </div>
            )}
            <div className="action_sheet__sections">
                {visibleSections.map((section) => (
                    <section className="action_sheet__section" key={section.id}>
                        {section.title != null && <h4 className="action_sheet__section_title">{section.title}</h4>}
                        <div className="action_sheet__items">
                            {section.items.map((item) => (
                                <ActionSheetRow key={item.id} item={item} context={context} onDrillIn={handleDrillIn} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
            {canGoBack && (
                <Button
                    className="action_sheet__back"
                    startSlot={<Icon src="/icons/common/arrow-left.svg" size="md" />}
                    onClick={goBack}
                    aria-label="Назад"
                    variant="primary"
                >
                    Вернуться назад
                </Button>
            )}
        </div>
    );
};
